import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ServiceAreasSection = () => {
  const { user } = useAuth();
  const [serviceAreas, setServiceAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    province: '',
    postalCodeRange: '',
    serviceRadiusKm: '',
    isPrimary: false,
  });
  const [error, setError] = useState('');

  const provinceOptions = [
    { value: 'Noord-Holland', label: 'Noord-Holland' },
    { value: 'Zuid-Holland', label: 'Zuid-Holland' },
    { value: 'Utrecht', label: 'Utrecht' },
    { value: 'Gelderland', label: 'Gelderland' },
    { value: 'Noord-Brabant', label: 'Noord-Brabant' },
    { value: 'Limburg', label: 'Limburg' },
    { value: 'Zeeland', label: 'Zeeland' },
    { value: 'Overijssel', label: 'Overijssel' },
    { value: 'Flevoland', label: 'Flevoland' },
    { value: 'Friesland', label: 'Friesland' },
    { value: 'Groningen', label: 'Groningen' },
    { value: 'Drenthe', label: 'Drenthe' },
  ];

  useEffect(() => {
    if (user?.id) {
      loadServiceAreas();
    }
  }, [user]);

  const loadServiceAreas = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase?.from('provider_service_areas')?.select('*')?.eq('provider_id', user?.id)?.order('is_primary', { ascending: false });

      if (fetchError) throw fetchError;
      setServiceAreas(data || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      setError('');
      const { error: insertError } = await supabase?.from('provider_service_areas')?.insert({
        provider_id: user?.id,
        province: formData?.province,
        postal_code_range: formData?.postalCodeRange,
        service_radius_km: parseInt(formData?.serviceRadiusKm),
        is_primary: formData?.isPrimary,
      });

      if (insertError) throw insertError;

      setFormData({
        province: '',
        postalCodeRange: '',
        serviceRadiusKm: '',
        isPrimary: false,
      });
      setEditing(false);
      loadServiceAreas();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDelete = async (areaId) => {
    try {
      const { error: deleteError } = await supabase?.from('provider_service_areas')?.delete()?.eq('id', areaId)?.eq('provider_id', user?.id);

      if (deleteError) throw deleteError;
      loadServiceAreas();
    } catch (err) {
      console.error('Error deleting service area:', err);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="h-20 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">Werkgebieden</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Beheer de gebieden waar u diensten levert
            </p>
          </div>
          {!editing && (
            <Button iconName="Plus" onClick={() => setEditing(true)}>
              Toevoegen
            </Button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-md">
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        {/* Add Form */}
        {editing && (
          <div className="mb-6 p-4 bg-muted/30 rounded-lg space-y-4">
            <h3 className="font-medium text-foreground">Nieuw Werkgebied</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Provincie"
                options={provinceOptions}
                value={formData?.province}
                onChange={(e) => setFormData({ ...formData, province: e?.target?.value })}
                required
              />
              <Input
                label="Postcode Bereik"
                placeholder="1000-1999"
                value={formData?.postalCodeRange}
                onChange={(e) => setFormData({ ...formData, postalCodeRange: e?.target?.value })}
              />
              <Input
                label="Service Straal (km)"
                type="number"
                placeholder="50"
                value={formData?.serviceRadiusKm}
                onChange={(e) => setFormData({ ...formData, serviceRadiusKm: e?.target?.value })}
                required
              />
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isPrimary"
                  checked={formData?.isPrimary}
                  onChange={(e) => setFormData({ ...formData, isPrimary: e?.target?.checked })}
                  className="h-4 w-4 rounded border-input"
                />
                <label htmlFor="isPrimary" className="text-sm text-foreground">
                  Primair werkgebied
                </label>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={handleAdd}>Opslaan</Button>
              <Button variant="outline" onClick={() => setEditing(false)}>
                Annuleren
              </Button>
            </div>
          </div>
        )}

        {/* Service Areas List */}
        <div className="space-y-3">
          {serviceAreas?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="MapPin" size={48} className="mx-auto mb-3 opacity-50" />
              <p>Nog geen werkgebieden toegevoegd</p>
            </div>
          ) : (
            serviceAreas?.map((area) => (
              <div
                key={area?.id}
                className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className="bg-primary/10 p-3 rounded-lg">
                    <Icon name="MapPin" size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium text-foreground">{area?.province}</h3>
                      {area?.is_primary && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                          Primair
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-4 mt-1 text-sm text-muted-foreground">
                      {area?.postal_code_range && (
                        <span>Postcode: {area?.postal_code_range}</span>
                      )}
                      {area?.service_radius_km && (
                        <span>Straal: {area?.service_radius_km} km</span>
                      )}
                    </div>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  iconName="Trash2"
                  onClick={() => handleDelete(area?.id)}
                />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceAreasSection;